import { CheckIcon } from 'lucide-react';

import type { LibraryItemView } from '@/lib/api/generated/model';
import type { UsedByEntry } from '../usedBy';
import { KindMonogram } from './KindMonogram';

/**
 * Which library workflows use this item, and how (spec 109 US4).
 *
 * Every kind has it, because "what uses this?" is the question behind every
 * removal and every edit, and until now only the removal confirmation asked
 * it. A workflow that uses the item only through another says which one. Each
 * entry opens that workflow's drawer, as a place Back returns from.
 *
 * Empty is said, not hidden: a section that simply is not there reads as one
 * still loading.
 */
export function UsedBy({
  entries,
  onOpenItem,
}: {
  entries: UsedByEntry[];
  onOpenItem?: (item: LibraryItemView) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Used by</h3>
      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No library workflow uses this.
        </p>
      ) : (
        <ul
          aria-label="Used by"
          className="border-border divide-border flex flex-col divide-y rounded-lg border"
        >
          {entries.map(({ user, through, present }) => (
            <li key={user.key}>
              <button
                type="button"
                disabled={onOpenItem === undefined}
                onClick={() => {
                  onOpenItem?.(user);
                }}
                className="hover:bg-accent/60 focus-visible:ring-ring flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:cursor-default disabled:hover:bg-transparent"
              >
                <KindMonogram kind="workflow" size="sm" />
                <span className="min-w-0 flex-1 truncate">
                  {user.name}
                  {through && (
                    <span className="text-muted-foreground">
                      {' '}
                      · through {through.name}
                    </span>
                  )}
                </span>
                {present && (
                  <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
                    {/* Lifted a pixel: flex centring sets it on the line box,
                        which includes descender room, so it sat low. */}
                    <CheckIcon
                      aria-hidden="true"
                      className="size-3 -translate-y-px"
                    />
                    already here
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
